import { get, noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import querystring from 'querystring';
import { NYPWC, Video } from '@zocdoc/components/src/icons';
import { green, navy, white } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import { breakpoint, ShowFor, HideFor } from '@zocdoc/components/src/grid';
import Lazyload from '@zocdoc/components/src/LazyLoad';
import TrackingPixel from '@zocdoc/components/src/TrackingPixel';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import {
    DoctorCardContainer,
    DoctorInfoContainer,
    DoctorInfoTopBar,
    DoctorName,
    DoctorSpecialty,
    SearchResultsNonBookableLink,
} from '../primitives';
import { DoctorPhoto, PhotoContainer } from '../DoctorPhoto/DoctorPhoto';
import SearchResultRow from '../SearchResultRow';
import SpoBanner from '../DoctorCard/SponsoredBanner';
import {
    INFO_TEXT_LINE_HEIGHT,
    STARS_BASE_SIZE,
    STARS_MEDIUM_SIZE,
} from '../constants';
import { ATTRIBUTION_TYPES, LOCALIZED_STRINGS } from './constants';
import TelehealthModal from './TelehealthModal';
import sendSpoAttribution from './sendSpoAttribution';

export class BaseTelehealthResult extends React.PureComponent {
    static propTypes = {
        adData: PropTypes.object,
        intl: PropTypes.object,
        isProduction: PropTypes.bool,
        shouldLinkToProfile: PropTypes.bool,
        onChangePhoneNumberInput: PropTypes.func,
        onClickAd: PropTypes.func,
        onClickCloseModal: PropTypes.func,
        onClickDownload: PropTypes.func,
        onClickSendSms: PropTypes.func,
        onScrolledIntoView: PropTypes.func,
        onViewAd: PropTypes.func,
        onViewSendSmsFailure: PropTypes.func,
        onViewSendSmsSuccess: PropTypes.func,
        provider: PropTypes.object,
        queryParams: PropTypes.object,
    };

    static defaultProps = {
        onChangePhoneNumberInput: noop,
        onClickAd: noop,
        onClickCloseModal: noop,
        onClickDownload: noop,
        onClickSendSms: noop,
        onScrolledIntoView: noop,
        onViewAd: noop,
        onViewSendSmsFailure: noop,
        onViewSendSmsSuccess: noop,
    };

    state = {
        showModal: false,
    };

    render() {
        const {
            adData,
            isProduction,
            onClickDownload,
            onClickSendSms,
            onClickCloseModal,
            onChangePhoneNumberInput,
            onScrolledIntoView,
            onViewSendSmsFailure,
            onViewSendSmsSuccess,
            ...props
        } = this.props;

        if (!get(adData, 'provider')) {
            return null;
        }

        const photoProvider = this.getPhotoProvider();

        return (
            <div>
                <TelResultRow
                    {...filterToValidAttributes(props)}
                    data-test="telehealth-result"
                    firstSlot={(
                        <TelDoctorCard onClick={this.handleClickStart}>
                            <DoctorCardContainer>
                                <Lazyload data-test="tel-lazyload-tracking-pixel">
                                    <TrackingPixel src={adData.spoServiceImpressionUrl} />
                                </Lazyload>
                                <PhotoContainer>
                                    <DoctorPhoto
                                        children={<OnlineIndicator />}
                                        isProduction={isProduction}
                                        provider={photoProvider}
                                    />
                                </PhotoContainer>
                                <DoctorInfoContainer>
                                    <SpoBanner isTelehealth={true} />
                                    <DoctorInfoTopBar>
                                        <DoctorName data-test="telehealth-prov-name">
                                            <span
                                                data-test="telehealth-prov-name-full"
                                                itemProp="name"
                                            >
                                                {adData.provider.fullName}
                                            </span>
                                        </DoctorName>
                                    </DoctorInfoTopBar>

                                    {
                                        adData.provider.readableProviderType && (
                                            <DoctorSpecialty
                                                data-test="doctor-card-info-specialty"
                                                itemProp="jobTitle"
                                            >
                                                {adData.provider.readableProviderType}
                                            </DoctorSpecialty>
                                        )
                                    }

                                    <LogoContainer>
                                        <NYPLogo />
                                    </LogoContainer>

                                    <HideFor
                                        xsmall
                                        xxsmall
                                    >
                                        {this.renderBlurb()}
                                    </HideFor>

                                </DoctorInfoContainer>
                            </DoctorCardContainer>

                            <ShowFor
                                xsmall
                                xxsmall
                            >
                                {this.renderBlurb()}
                            </ShowFor>
                        </TelDoctorCard>
                    )}
                    lastSlot={this.renderVisitButton()}
                    onScrolledIntoView={onScrolledIntoView}
                    searchResultId={adData.searchResultId}
                />

                {this.state.showModal ? <TelehealthModal
                    branchDeepLink={adData.provider.branchDeepLink}
                    onChangePhoneNumberInput={onChangePhoneNumberInput}
                    onClickCancel={this.handleClickModalClose}
                    onClickDownload={onClickDownload}
                    onClickSendSms={onClickSendSms}
                    onViewSendSmsFailure={onViewSendSmsFailure}
                    onViewSendSmsSuccess={onViewSendSmsSuccess}
                /> : null}
            </div>
        );
    }

    componentDidMount() {
        this.props.onViewAd();
    }

    getPhotoProvider() {
        const { adData } = this.props;

        const photoProvider = {};

        if (adData.provider.providerImage != null) {
            photoProvider.squarePictureUrl = this.getImageSource(adData.provider.providerImage);
        }

        return photoProvider;
    }

    getImageSource(providerImage) {
        // In production images are base-64 encoded UTF-8, but screenshot test just uses path to local file
        return (global.screenshot) ? providerImage : `data:image/jpeg;charset=utf-8;base64,${providerImage}`;
    }

    renderBlurb() {
        const {
            intl,
        } = this.props;

        return (
            <Blurb
                data-test="telehealth-blurb"
            >
                <Hook>
                    {intl.formatMessage(LOCALIZED_STRINGS.AdHook)}
                </Hook>
                <span>
                    {intl.formatMessage(LOCALIZED_STRINGS.AdBlurb)}
                </span>
            </Blurb>
        );
    }

    renderVisitButton() {
        return (
            <StartButtonContainer>
                <StartButton
                    data-test="telehealth-ad-button"
                    onClick={this.handleClickStart}
                >
                    <FormattedMessage {...LOCALIZED_STRINGS.AdStartButtonMessage} />
                    <VideoIcon />
                </StartButton>
            </StartButtonContainer>
        );
    }

    handleClickModalClose = e => {
        this.props.onClickCloseModal(e);

        this.setState({
            showModal: false,
        });
    };

    handleClickStart = async e => {
        const {
            adData,
            shouldLinkToProfile,
            onClickAd,
            queryParams,
        } = this.props;

        onClickAd(e);
        try {
            await sendSpoAttribution(adData, ATTRIBUTION_TYPES.click);
        } catch (err) {
            // Fire and forget
        }

        if (shouldLinkToProfile) {
            const queryString = querystring.stringify(queryParams);
            window.location.assign(`/videovisit?${queryString}`);
        } else {
            this.setState({
                showModal: true,
            });
        }
    };
}

const Blurb = styled.div`
    color: ${navy};
    display: block;
    font-family: ${FontFamily.SharpSansMedium};
    font-size: 14px;
    line-height: ${INFO_TEXT_LINE_HEIGHT}px;
    padding: 0;
`;

const Hook = styled.span`
   font-family: ${FontFamily.SharpSansSemibold};
`;

const NYPLogo = styled(NYPWC)`
    font-size: 300%;
    margin-top: 10px;

    ${breakpoint.medium} {
        font-size: 250%;
    }
`;

const LogoContainer = styled.div`
    font-size: ${STARS_BASE_SIZE}px;

    ${breakpoint.medium} {
        font-size: ${STARS_MEDIUM_SIZE}px;
    }
`;

const OnlineIndicator = styled.div`
    background: ${green};
    border: solid 2px ${white};
    border-radius: 50%;
    bottom: 3%;
    height: 11px;
    position: absolute;
    right: 3%;
    width: 11px;

    ${breakpoint.large} {
        bottom: 5%;
        height: 13px;
        right: 5%;
        width: 13px;
    }
`;

const StartButton = styled(SearchResultsNonBookableLink)`
    cursor: pointer;
`;

const StartButtonContainer = styled.div`
    align-items: center;
    display: flex;
    flex-basis: 100%;
    flex-direction: row;
    justify-content: center;
    margin-right: 15px;
    width: 100%;

    ${breakpoint.small} {
        margin-right: 0px;
    }
`;

const TelResultRow = styled(SearchResultRow)`
    border-bottom: 4px solid ${navy};
`;

const TelDoctorCard = styled.div`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    position: relative;
`;

const VideoIcon = styled(Video)`
    display: inline-block;
    margin-bottom: 2px !important;
    margin-left: 10px;
    margin-right: 12px;
    vertical-align: middle;
`;

export default injectIntl(BaseTelehealthResult);
